import { Injectable } from '@angular/core'
import { RegionalPermissionService } from '@ftr/api-regional'
import { ResourceType } from '@ftr/contracts/api/permissions'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { DataStore, Uuid } from '@ftr/contracts/type/shared'
import { ApiResult } from '@ftr/foundation'
import { PermissionService } from './permissions'

/**
 * The purpose of this service is to ensure we call the correct api for permission checks, that is
 * core api for court recordings and regional api for live stream recordings.
 */
@Injectable()
export class PermissionProxyService {
  constructor(
    private readonly permissionService: PermissionService,
    private readonly regionalPermissionService: RegionalPermissionService,
  ) {}

  hasPermission(
    dataStore: DataStore,
    courtSystemId: Uuid,
    userGroupPermissionId: UserGroupPermissionId,
    resourceType?: ResourceType,
    resourceIds?: Uuid[],
  ): ApiResult<boolean> {
    if (dataStore === DataStore.Core) {
      return this.permissionService.hasPermission(courtSystemId, userGroupPermissionId, resourceType, resourceIds)
    }
    if (dataStore === DataStore.Regional) {
      return this.regionalPermissionService.hasPermission(
        courtSystemId,
        userGroupPermissionId,
        resourceType,
        resourceIds,
      )
    }
    return ApiResult.success(false)
  }

  hasAnyPermission(
    dataStore: DataStore,
    courtSystemId: Uuid,
    userGroupPermissionIds: UserGroupPermissionId[],
    resourceType?: ResourceType,
    resourceIds?: Uuid[],
  ): ApiResult<boolean> {
    if (dataStore === DataStore.Core) {
      return this.permissionService.hasAnyPermission(courtSystemId, userGroupPermissionIds, resourceType, resourceIds)
    }
    if (dataStore === DataStore.Regional) {
      return this.regionalPermissionService.hasAnyPermission(
        courtSystemId,
        userGroupPermissionIds,
        resourceType,
        resourceIds,
      )
    }
    return ApiResult.success(false)
  }

  hasRecordingPermission(
    dataStore: DataStore,
    courtSystemId: Uuid,
    recordingId: Uuid,
    userGroupPermissionId: UserGroupPermissionId,
  ): ApiResult<boolean> {
    return this.hasPermission(dataStore, courtSystemId, userGroupPermissionId, 'recording', [recordingId])
  }

  hasAnyRecordingPermission(
    dataStore: DataStore,
    courtSystemId: Uuid,
    recordingId: Uuid,
    userGroupPermissionIds: UserGroupPermissionId[],
  ): ApiResult<boolean> {
    return this.hasAnyPermission(dataStore, courtSystemId, userGroupPermissionIds, 'recording', [recordingId])
  }
}
